import { useState, useEffect } from 'react'

import axios from 'axios'

import useSentry from 'Pods/Shared/Hooks/useSentry'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

const DEFAULT_MESSAGES = { data: EMPTY_ARRAY, included: EMPTY_ARRAY, totalUnreadMessages: 0 }
const DEFAULT_NOTIFICATIONS = { data: EMPTY_ARRAY, feedSize: 0, isFeedMaintenance: false }

function useActionsData(isSignedIn) {
  const { captureException } = useSentry()

  const [cart, setCart] = useState({
    data: [],
    meta: {},
    included: [],
    page: 1,
    loading: false,
    isLastPageLoaded: true,
  })

  const [messages, setMessages] = useState(DEFAULT_MESSAGES)

  const [notifications, setNotifications] = useState(DEFAULT_NOTIFICATIONS)

  const [boostCredits, setBoostCredits] = useState(0)
  const [userModelsPublished, setUserModelsPublished] = useState(0)

  useEffect(() => {
    fetchActionsData()

    /* eslint-disable-next-line no-undef */
    App.events.subscribe('app:cart:item:remove', onCartItemRemove)
    /* eslint-disable-next-line no-undef */
    App.events.subscribe('app:cart:added', onCartItemAdd)
    /* eslint-disable-next-line no-undef */
    App.events.subscribe('app:cart:refresh', onCartItemAdd)
    /* eslint-disable-next-line no-undef */
    App.events.subscribe('app:inbox:refresh', fetchInboxData)
    /* eslint-disable-next-line no-undef */
    App.events.subscribe('update-boost-credits', updateBoostCredits)
  }, [isSignedIn])

  function fetchActionsData() {
    fetchCartData()

    if (isSignedIn) {
      fetchNotificationsData()
      fetchInboxData()
      fetchBoostCredits()
    }
  }

  function fetchNotificationsData() {
    return axios
      .get('/api/internal/users/notifications')
      .then((response) => onFetchNotificationsSuccess(response))
      .catch((error) => {
        setNotifications(DEFAULT_NOTIFICATIONS)
        reportFetchError(error, 'notifications')
      })
  }

  function fetchInboxData() {
    axios
      .get('/api/internal/conversations/user_messages')
      .then((response) => {
        const meta = response.data?.meta || {}

        setMessages({
          data: response.data?.data || EMPTY_ARRAY,
          included: response.data?.included || EMPTY_ARRAY,
          totalUnreadMessages: meta.totalUnreadMessages || 0,
        })
      })
      .catch((error) => reportFetchError(error, 'inbox'))
  }

  // A failed count endpoint leaves the badge on its last value, which is indistinguishable
  // from a healthy one, so this report is the only signal the failure leaves.
  function reportFetchError(error, section) {
    captureException(error, {
      tags: { section: 'UnifiedNavigation', component: 'useActionsData', event: section },
      extra: { isSignedIn, url: typeof window !== 'undefined' ? window.location.href : undefined },
    })
  }

  function fetchBoostCredits() {
    axios
      .get('/api/internal/boost-sales')
      .then((response) => {
        setBoostCredits(response.data.boost_credits || 0)
        setUserModelsPublished(response.data.items_count || 0)
      })
      .catch(() => {
        setBoostCredits(0)
        setUserModelsPublished(0)
      })
  }

  function onFetchNotificationsSuccess(response) {
    const meta = response.data?.meta || {}

    setNotifications({
      data: response.data?.data || EMPTY_ARRAY,
      feedSize: meta.size || 0,
      isFeedMaintenance: Boolean(meta.isMaintenance),
    })
  }

  function fetchCartData(pageNr = 1) {
    if (cart.loading || (pageNr !== 1 && cart.isLastPageLoaded)) {
      return false
    }

    setCartDataPageIsLoading()

    return axios
      .get('/api/internal/cg/cart_items', { params: { page: pageNr } })
      .then((response) => onFetchCartSuccess(response, pageNr))
      .catch(() => {
        setCart((prevCart) => ({ 
          ...prevCart, 
          loading: false, 
          isLastPageLoaded: true,
        }))
      })
  }

  function setCartDataPageIsLoading() {
    setCart((prevCart) => ({ ...prevCart, loading: true }))
  }


  function fetchNextCartDataPage() {
    return fetchCartData(cart.page + 1)
  }

  function onCartItemRemove(cartItem) {
    const removableId = cartItem.attributes.itemId
    const currentCartItems = window?.pageConfig?.cart_items || []

    if (currentCartItems.length) {
      window.pageConfig.cart_items = currentCartItems.filter((el) => {
        return el.id !== removableId
      })
    }

    return axios
      .delete(`/api/internal/${cartItem.attributes.kind}/items/${cartItem.attributes.itemId}/cart_items/${cartItem.id}`)
      .then(() => fetchCartData(1))
      .catch((error) => {
        reportFetchError(error, 'cartItemRemove')
        return fetchCartData(1)
      })
  }

  function onCartItemAdd() {
    fetchCartData(1)
  }

  function updateBoostCredits(creditsData) {
    const newBoostCreditsCount = creditsData.boostCredits || creditsData
    setBoostCredits(newBoostCreditsCount)
  }

  function onFetchCartSuccess(response, pageNr) {
    /* eslint-disable-next-line no-undef */
    App.events.publish('app:cart:updated', response.data)

    const responseData = response.data?.data || []
    const responseIncluded = response.data?.included || []
    const responseMeta = response.data?.meta || {}
    const isFirstPage = pageNr === 1

    const mergedData = isFirstPage ? responseData : cart.data.concat(responseData)
    const mergedIncluded = isFirstPage ? responseIncluded : (cart.included || []).concat(responseIncluded)
    const resolvedMeta = isFirstPage ? responseMeta : cart.meta
    const resolvedMetaSize = Number(resolvedMeta?.size)
    const totalItems = Number.isFinite(resolvedMetaSize) ? resolvedMetaSize : mergedData.length
    const reachedEnd = responseData.length === 0 || mergedData.length >= totalItems

    setCart({
      data: mergedData,
      meta: resolvedMeta,
      included: mergedIncluded,
      page: pageNr,
      loading: false,
      isLastPageLoaded: reachedEnd,
    })
  }

  return {
    cart,
    messages,
    notifications,
    fetchNextCartDataPage,
    boostCredits,
    userModelsPublished,
  }
}

export { useActionsData }
